import type { VoteDetail, VoteQuestion } from "@/lib/types";
import Avatar from "@/components/base/Avatar";

interface Props {
  data: VoteDetail;
}

function OptionBars({ q }: { q: VoteQuestion }) {
  const options = q.options ?? [];
  if (options.length === 0) {
    return <p className="mt-3 text-sm text-foreground-400">보기가 없습니다.</p>;
  }
  const my = q.my?.opts ?? [];
  return (
    <div className="mt-3 space-y-2.5">
      {options.map((opt) => {
        const mine = my.includes(opt.no);
        const pct = Math.max(0, Math.min(100, opt.pct ?? 0));
        return (
          <div
            key={opt.no}
            className={`rounded-lg border p-3 ${
              mine ? "border-primary-300 bg-primary-50" : "border-background-200 bg-background-50"
            }`}
          >
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-1.5 font-medium text-foreground-800">
                {mine && (
                  <i className="ri-check-line text-primary-600" aria-hidden="true"></i>
                )}
                {opt.text}
              </span>
              <span className="shrink-0 tabular-nums text-foreground-500">
                {opt.cnt}명 · {opt.pct}%
              </span>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-background-100">
              <div
                className={`h-full rounded-full ${mine ? "bg-primary-500" : "bg-secondary-400"}`}
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              ></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScaleResult({ q }: { q: VoteQuestion }) {
  const options = q.options ?? [];
  const my = q.my?.score ?? 0;
  return (
    <div className="mt-3">
      <div className="flex items-end gap-2">
        <span className="font-heading text-3xl font-bold tabular-nums text-primary-700">
          {(q.avg ?? 0).toFixed(1)}
        </span>
        <span className="pb-1 text-sm text-foreground-500">/ 5</span>
        <span className="ml-auto pb-1 text-xs text-foreground-400">
          {q.total ?? 0}명 응답
        </span>
      </div>
      {my > 0 && (
        <p className="mt-1.5 text-xs text-primary-700">내 응답 {my}점</p>
      )}
      {options.length > 0 && (
        <div className="mt-4 space-y-2">
          {options.map((opt) => {
            const pct = Math.max(0, Math.min(100, opt.pct ?? 0));
            return (
              <div key={opt.no} className="flex items-center gap-3">
                <span className="w-4 shrink-0 text-xs font-semibold text-foreground-600">
                  {opt.text}
                </span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-background-100">
                  <div
                    className="h-full rounded-full bg-secondary-400"
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                  ></div>
                </div>
                <span className="w-16 shrink-0 text-right text-xs tabular-nums text-foreground-500">
                  {opt.cnt}명 · {opt.pct}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TextAnswers({ q, anon }: { q: VoteQuestion; anon: boolean }) {
  const texts = q.texts ?? [];
  if (texts.length === 0) {
    return <p className="mt-3 text-sm text-foreground-400">아직 응답이 없습니다.</p>;
  }
  return (
    <ul className="mt-3 space-y-3">
      {texts.map((t, ti) => {
        const w = t.writer;
        const realName =
          [w?.aho, w?.name].filter(Boolean).join(" ") ||
          [t.aho, t.name].filter(Boolean).join(" ") ||
          "회원";
        const name = anon ? "익명" : realName;
        const photo = anon ? undefined : w?.photo || t.photo;
        return (
          <li
            key={t.no ?? ti}
            className="rounded-lg border border-background-200 bg-background-100/50 p-3.5"
          >
            <div className="flex items-center gap-2.5">
              <Avatar name={anon ? "익" : realName} photo={photo} size={28} />
              <span className="text-sm font-semibold text-foreground-700">{name}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
              {t.content}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

// 결과 영역 — can_result === 1 일 때만 상위에서 렌더링한다.
// (can_result === 0 이면 서버가 cnt/pct 를 0 으로 내려주므로 추정해서 그리지 않는다)
export default function VoteResult({ data }: Props) {
  const anon = data.is_anon === 1;
  const questions = data.questions ?? [];
  return (
    <div className="space-y-5">
      {questions.map((q, i) => (
        <div
          key={q.no}
          className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5"
        >
          <h3 className="font-heading text-base font-bold text-foreground-950">
            <span className="mr-1.5 text-foreground-400">{i + 1}.</span>
            {q.title}
          </h3>
          {(q.qtype === "single" || q.qtype === "multi") && <OptionBars q={q} />}
          {q.qtype === "scale" && <ScaleResult q={q} />}
          {q.qtype === "text" && <TextAnswers q={q} anon={anon} />}
        </div>
      ))}
    </div>
  );
}